/**
 * CalendarScreen — Calendrier de tenues v1.12.190.
 *
 * MVP :
 *  - Header avec navigation mois précédent / suivant
 *  - Grille mensuelle 7 colonnes (lun-dim)
 *  - Chaque jour avec entrée → vignette mini photo
 *  - Tap sur un jour → modal détail (si entry) ou "Ajouter" (si vide)
 *  - Free tier : grille montre les 7 derniers jours uniquement
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View, Text, ScrollView, TouchableOpacity, Image, ActivityIndicator,
  StyleSheet, Alert, Platform, useWindowDimensions,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../context/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { listMonth, deleteEntry } from '../services/CalendarService';
import {
  getCalendarMonthView, computeMonthStats, shouldShowFirstTimeHelp,
  getOccasionDisplay, getWeatherIcon,
} from '../services/CalendarAgentService';
import { showAppAlert } from '../components/AppDialog';
import CalendarIcon from '../components/CalendarIcon';
import AddOutfitToCalendarModal from '../components/AddOutfitToCalendarModal';
import CalendarDayDetailModal from '../components/CalendarDayDetailModal';
import CalendarFirstTimeHelp from '../components/CalendarFirstTimeHelp';
import CalendarMonthStats from '../components/CalendarMonthStats';
import { useI18n } from '../i18n';

const C = {
  bg: '#FAF8F4', card: '#FFFFFF', text: '#1A1A1A',
  textMuted: '#6B6B6B', textFaint: '#A0A0A0',
  border: '#EAE6DF', gold: '#D4AF72', goldDark: '#B8860B',
  red: '#E53935', green: '#4CAF50',
};

const MONTH_NAMES_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_LETTER_KEYS = ['wd_mon', 'wd_tue', 'wd_wed', 'wd_thu', 'wd_fri', 'wd_sat', 'wd_sun'];

// v1.12.276 — i18n month label (lang-aware via toLocaleDateString)
function localeForLang(lang) {
  switch (lang) {
    case 'en': return 'en-US';
    case 'es': return 'es-ES';
    case 'it': return 'it-IT';
    case 'de': return 'de-DE';
    case 'pt': return 'pt-PT';
    case 'fr': return 'fr-FR';
    default:   return undefined; // browser default
  }
}
function getMonthLabel(year, monthIdx, lang) {
  try {
    const d = new Date(year, monthIdx, 1);
    const locale = localeForLang(lang);
    const formatted = d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    // Capitalize first letter (FR/IT/ES return lowercase month names by default)
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  } catch {
    return `${MONTH_NAMES_FR[monthIdx]} ${year}`;
  }
}

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }

function monthIso(year, monthIdx /* 0-11 */) {
  return `${year}-${pad2(monthIdx + 1)}`;
}

export default function CalendarScreen({ navigation }) {
  const { user, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  const insets = useSafeAreaInsets();
  const { height: winHeight } = useWindowDimensions();
  // v1.12.228 — padding top pour ne pas coller à la barre système / WebShell
  const topPadding = Math.max(insets.top, 12) + 4;
  const [now, setNow] = useState(new Date());
  // v1.12.225 — modal d'ajout (3 sources : Tenue du jour / Mes Looks / Manuel)
  const [addModalDate, setAddModalDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // v1.12.986 — date tapée surlignée
  // v1.12.236 — modal détail journée
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailDateLabel, setDetailDateLabel] = useState('');
  const [homeOutfit, setHomeOutfit] = useState(null);
  const [homeOutfitOptions, setHomeOutfitOptions] = useState([]); // v1.12.243 — top 5 alternatives
  const [myLooks, setMyLooks] = useState([]);
  const [looksIA, setLooksIA] = useState([]);
  const [wardrobe, setWardrobe] = useState([]); // v1.12.246 — pour filtre par article
  // Charger les looks perso (DB via LookService + cache AsyncStorage) + dernière Tenue du jour + Looks IA
  // v1.12.289 — bascule sur LookService pour ne pas dépendre du cache local uniquement
  React.useEffect(() => {
    const uid = user?.id || 'guest';
    (async () => {
      try {
        const { listLooks } = require('../services/LookService');
        const list = await listLooks({ user, getValidToken });
        setMyLooks(list || []);
      } catch (e) { console.warn('[Calendar] listLooks failed:', e?.message); }
    })();
    AsyncStorage.getItem(`outfit_today_recommendation_${uid}`).then(raw => {
      if (raw) { try { setHomeOutfit(JSON.parse(raw)); } catch {} }
    }).catch(() => {});
    // v1.12.243 — top 5 options pour bouton Régénérer
    AsyncStorage.getItem(`outfit_today_options_${uid}`).then(raw => {
      if (raw) { try { setHomeOutfitOptions(JSON.parse(raw) || []); } catch {} }
    }).catch(() => {});
    // v1.12.238 — Looks IA persistés par LooksScreen
    AsyncStorage.getItem(`outfit_looks_ia_${uid}`).then(raw => {
      if (raw) { try { setLooksIA(JSON.parse(raw) || []); } catch {} }
    }).catch(() => {});
    // v1.12.246 — Wardrobe pour filtre Looks IA par article (photo+pseudo)
    if (user?.id) {
      const tokenP = getValidToken ? getValidToken() : Promise.resolve(user.accessToken);
      tokenP.then(token => {
        const { getWardrobe } = require('../services/DatabaseService');
        return getWardrobe(user.id, token);
      }).then(w => {
        // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier+bonus)
        // pour qu'ils n'apparaissent pas dans le calendrier
        if (Array.isArray(w)) setWardrobe(w.filter(it => !it._hidden));
      }).catch(() => {});
    }
  }, [user?.id]);
  const [year, setYear] = useState(now.getFullYear());
  const [monthIdx, setMonthIdx] = useState(now.getMonth());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coverage, setCoverage] = useState(0);
  const [tier, setTier] = useState('free');
  const [historyDays, setHistoryDays] = useState(7);
  // v1.12.233 — Stats du mois + bandeau aide première fois
  const [monthStats, setMonthStats] = useState(null);
  const [helpBanner, setHelpBanner] = useState(null);
  // v1.12.243+251 — map { date → snapshot } pour afficher l'image principale du look
  // dans les cellules calendrier (snapshot local sauvegardé à l'ajout).
  // useFocusEffect : reload à chaque entrée sur l'écran (depuis Home/Looks/Wardrobe
  // après ajout via raccourci, le snapshot vient d'être écrit dans AsyncStorage).
  // v1.12.253 — Init synchrone depuis localStorage sur web (pas de race condition avec
  // setState async). Sur native AsyncStorage écrit en JS bridge donc pas de localStorage,
  // on fait un read async via useEffect en complément.
  const [snapshotsMap, setSnapshotsMap] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage && user?.id) {
        const raw = window.localStorage.getItem(`calendar_snapshots_${user.id}`);
        if (raw) return JSON.parse(raw) || {};
      }
    } catch {}
    return {};
  });
  const reloadSnapshots = useCallback(() => {
    if (!user?.id) return;
    // v1.12.540 — Recharge AUSSI le snapshot Looks IA + My Looks à chaque focus
    // (sinon si user visite LooksScreen après Calendar, looksIA reste vide)
    AsyncStorage.getItem(`outfit_looks_ia_${user.id}`).then(raw => {
      if (raw) { try { setLooksIA(JSON.parse(raw) || []); } catch {} }
    }).catch(() => {});
    try {
      const { listLooks } = require('../services/LookService');
      listLooks({ user, getValidToken }).then(list => {
        if (Array.isArray(list)) setMyLooks(list);
      }).catch(() => {});
    } catch {}
    // Web : read localStorage synchronously
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = window.localStorage.getItem(`calendar_snapshots_${user.id}`);
        if (raw) { try { setSnapshotsMap(JSON.parse(raw) || {}); return; } catch {} }
        setSnapshotsMap({});
        return;
      }
    } catch {}
    // Native : fallback async
    AsyncStorage.getItem(`calendar_snapshots_${user.id}`).then(raw => {
      if (raw) { try { setSnapshotsMap(JSON.parse(raw) || {}); } catch {} }
      else setSnapshotsMap({});
    }).catch(() => {});
  }, [user, getValidToken]);
  // Initial + après changement entries
  // v1.13.39 — FIX clignotement : deps stables (user.id, entries.length) au lieu
  // de `reloadSnapshots` dont la ref change à chaque render (user objet +
  // getValidToken non mémoïsés) → évite la boucle de re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reloadSnapshots(); }, [user?.id, entries.length]);
  // Vérifier si on doit afficher le bandeau d'aide (1 fois max par user)
  React.useEffect(() => {
    if (!user?.id) return;
    shouldShowFirstTimeHelp({ userId: user.id }).then(r => {
      if (r?.success && r.data?.should_show && r.data.banner) {
        setHelpBanner(r.data.banner);
      }
    }).catch(() => {});
  }, [user?.id]);

  // v1.12.808 — callbacks stables pour AddOutfitToCalendarModal (sinon AppModal web cycle history.back → ferme modal au moindre re-render → empeche selection look IA)
  const handleAddModalClose = useCallback(() => { setAddModalDate(null); }, []);
  const handleAddModalAdded = useCallback(() => { setAddModalDate(null); loadMonthRef.current?.(); }, []);
  const loadMonthRef = useRef(null);
  const reloadSnapshotsRef = useRef(null); // v1.13.40 — pour l'appeler depuis useFocusEffect stable

  const loadMonth = useCallback(async () => {
    if (!user) return; // gate géré par le rendu (voir return early plus bas)
    setLoading(true);
    try {
      const token = getValidToken ? await getValidToken() : user.accessToken;
      const r = await listMonth({ month: monthIso(year, monthIdx), authToken: token });
      const d = r?.data || {};
      const list = Array.isArray(d.entries) ? d.entries : [];
      setEntries(list);
      setCoverage(d.coverage_percentage || 0);
      const userTier = user?.tier || d.tier || 'free';
      setTier(userTier);
      setHistoryDays(d.history_days);
      // v1.12.233 — calcul stats client-side (basic+ uniquement)
      if (userTier !== 'free') {
        try {
          const stats = computeMonthStats(list, year, monthIdx + 1);
          setMonthStats(stats);
        } catch { setMonthStats(null); }
      } else {
        setMonthStats(null);
      }
    } catch (e) {
      console.warn('[Calendar] loadMonth:', e.message);
      setEntries([]);
      setMonthStats(null);
    } finally {
      setLoading(false);
    }
  }, [user, year, monthIdx, getValidToken]);

  // v1.13.39 — FIX clignotement calendrier : ne plus dépendre de `loadMonth`
  // (dont la ref change à chaque render car getValidToken n'est pas mémoïsé dans
  // AuthContext) → l'effet se relançait en boucle (setLoading true/false) →
  // calendrier qui apparaît/disparaît + cases non cliquables. On dépend
  // uniquement des données réelles (user, mois affiché).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMonth(); }, [user?.id, year, monthIdx]);
  // keep refs to latest callbacks so le useFocusEffect stable peut les appeler
  useEffect(() => { loadMonthRef.current = loadMonth; }, [loadMonth]);
  useEffect(() => { reloadSnapshotsRef.current = reloadSnapshots; }, [reloadSnapshots]);

  // v1.12.251 — useFocusEffect : refresh entries + snapshots quand on revient
  // sur l'écran (depuis Home/Looks/Wardrobe via raccourci calendrier).
  // v1.13.40 — FIX clignotement (cause principale) : les deps [reloadSnapshots,
  // loadMonth] étaient INSTABLES (ces callbacks changent à chaque render car
  // user objet + getValidToken non mémoïsés) → le callback focus se relançait à
  // CHAQUE render tant que l'écran était focus → boucle loadMonth (setLoading
  // true/false) → calendrier qui clignote. On appelle via refs avec deps [] (stable).
  useFocusEffect(useCallback(() => {
    reloadSnapshotsRef.current?.();
    loadMonthRef.current?.();
  }, []));

  // Map date → entry pour lookup rapide
  const entriesByDate = useMemo(() => {
    const map = new Map();
    for (const e of entries) map.set(e.date, e);
    return map;
  }, [entries]);

  // Calcul de la grille du mois
  const monthGrid = useMemo(() => {
    const firstDay = new Date(year, monthIdx, 1);
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    // JS: 0=dimanche, 6=samedi → nous voulons commencer lundi (0=L … 6=D)
    const dowMon = (firstDay.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < dowMon; i++) cells.push(null); // padding début
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        day: d,
        dateStr: `${year}-${pad2(monthIdx + 1)}-${pad2(d)}`,
      });
    }
    while (cells.length % 7 !== 0) cells.push(null); // padding fin
    return cells;
  }, [year, monthIdx]);

  const goPrevMonth = () => {
    if (monthIdx === 0) { setMonthIdx(11); setYear(year - 1); }
    else setMonthIdx(monthIdx - 1);
  };
  const goNextMonth = () => {
    if (monthIdx === 11) { setMonthIdx(0); setYear(year + 1); }
    else setMonthIdx(monthIdx + 1);
  };

  const handleDayPress = (cell) => {
    if (!cell) return;
    setSelectedDate(cell.dateStr); // v1.12.986 — surligne la date choisie
    const entry = entriesByDate.get(cell.dateStr);
    if (entry) {
      // v1.12.236 — ouvre la modal détail journée (vue complète : photo + items + notes + supprimer)
      const dateObj = new Date(year, monthIdx, cell.day);
      const label = dateObj.toLocaleDateString(localeForLang(lang) || 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      setDetailEntry(entry);
      setDetailDateLabel(label);
    } else {
      // v1.12.225 — ouvre la modal AddOutfitToCalendarModal
      const d = new Date(year, monthIdx, cell.day);
      setAddModalDate(d);
    }
  };

  const isToday = (cell) => {
    if (!cell) return false;
    const t = new Date();
    return cell.day === t.getDate() && monthIdx === t.getMonth() && year === t.getFullYear();
  };

  // v1.12.192 — sizing en % (14.28% = 1/7) au lieu de pixels fixes basés sur
  // SCREEN_WIDTH. Sur web, SCREEN_WIDTH = viewport entier (~1920) alors que le
  // contenu est contraint dans WebShell à 720px max → produisait 3 cells/ligne.
  // En pourcentage, chaque cell prend 1/7 du parent, peu importe sa largeur réelle.

  // v1.12.194 — gate auth : calendrier accessible uniquement aux utilisateurs
  // connectés. Si pas de user → écran d'invitation à se connecter.
  if (!user) {
    return (
      <SafeAreaView style={{ flex: 1, minHeight: 0, backgroundColor: C.bg }} edges={['top','left','right']}>
        <View style={[styles.header, { paddingTop: topPadding }]}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backBtn}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
            activeOpacity={0.6}>
            <Text style={styles.backIcon}>‹</Text>
          </TouchableOpacity>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <CalendarIcon size={20} color={C.text} dot="#D4AF72" />
            <Text style={styles.headerTitle}>{t('my_calendar_short')}</Text>
          </View>
          <View style={{ width: 40 }} />
        </View>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <Text style={{ fontSize: 48, marginBottom: 16 }}>🔒</Text>
          <Text style={{ fontSize: 18, fontWeight: '700', color: C.text, marginBottom: 8, textAlign: 'center' }}>
            {t('login_required')}
          </Text>
          <Text style={{ fontSize: 13, color: C.textMuted, textAlign: 'center', marginBottom: 24, lineHeight: 20 }}>
            {t('calendar_login_desc')}
          </Text>
          <TouchableOpacity
            onPress={() => navigation.navigate('Login')}
            activeOpacity={0.85}
            style={{
              backgroundColor: C.text, paddingVertical: 14, paddingHorizontal: 32,
              borderRadius: 10, borderWidth: 1, borderColor: C.gold,
            }}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', color: C.gold, letterSpacing: 0.5 }}>
              {t('sign_in_caps')}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, minHeight: 0, backgroundColor: C.bg }} edges={['top','left','right']}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: topPadding }]}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backBtn}
          hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
          activeOpacity={0.6}>
          <Text style={styles.backIcon}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('my_calendar_title')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        showsVerticalScrollIndicator={true}
        contentContainerStyle={{ paddingBottom: 120 }}
        style={Platform.OS === 'web' ? { height: winHeight - 60, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}>
        {/* Stats banner */}
        <View style={styles.statsBanner}>
          <Text style={styles.statsText}>
            {t(entries.length > 1 ? 'month_progress_other' : 'month_progress_one')
              .replace('{percent}', String(coverage))
              .replace('{count}', String(entries.length))}
          </Text>
          {historyDays != null && (
            <Text style={styles.tierNote}>
              {t('tier_history_limit')
                .replace('{tier}', String(tier))
                .replace('{days}', String(historyDays))}
            </Text>
          )}
        </View>

        {/* Month nav */}
        <View style={styles.monthNav}>
          <TouchableOpacity onPress={goPrevMonth} style={styles.navBtn}>
            <Text style={styles.navBtnText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthLabel}>
            {getMonthLabel(year, monthIdx, lang)}
          </Text>
          <TouchableOpacity onPress={goNextMonth} style={styles.navBtn}>
            <Text style={styles.navBtnText}>›</Text>
          </TouchableOpacity>
        </View>

        {/* Day-of-week header (aligné sur la grille via flex 1/7) */}
        <View style={styles.dowRow}>
          {DAY_LETTER_KEYS.map((k, i) => (
            <View key={i} style={styles.dowCell}>
              <Text style={styles.dowLetter}>{t(k)}</Text>
            </View>
          ))}
        </View>

        {/* Grid */}
        {loading ? (
          <ActivityIndicator size="small" color={C.gold} style={{ marginTop: 32 }} />
        ) : (
          <View style={styles.grid}>
            {monthGrid.map((cell, i) => {
              if (!cell) return (
                <View key={i} style={styles.cellWrap}>
                  <View style={{ flex: 1 }} />
                </View>
              );
              const entry = entriesByDate.get(cell.dateStr);
              const today = isToday(cell);
              return (
                <View key={i} style={styles.cellWrap}>
                  <TouchableOpacity
                    onPress={() => handleDayPress(cell)}
                    activeOpacity={0.85}
                    style={[
                      styles.cell,
                      entry && styles.cellWithEntry,
                      today && styles.cellToday,
                      cell.dateStr === selectedDate && { borderWidth: 2, borderColor: C.goldDark },
                    ]}
                  >
                    {/* v1.12.243+257 — Image cellule. Source 1 : entry.items (Lambda joint
                        wardrobe/products). Source 2 : snapshot local. Priorité Robes>Hauts>... */}
                    {(() => {
                      if (entry?.user_photo_url) {
                        return <Image source={{ uri: entry.user_photo_url }} style={styles.cellImage} resizeMode="contain" />;
                      }
                      if (entry?.composite_image_url) {
                        return <Image source={{ uri: entry.composite_image_url }} style={styles.cellImage} resizeMode="cover" />;
                      }
                      const PRIORITY = ['Robes', 'Hauts', 'Manteaux', 'Vestes', 'Bas', 'Chaussures'];
                      const findHero = (items) => {
                        if (!Array.isArray(items) || items.length === 0) return null;
                        for (const cat of PRIORITY) {
                          const h = items.find(it => it && it.category === cat && it.image_url);
                          if (h) return h;
                        }
                        return items.find(it => it && it.image_url) || null;
                      };
                      // Source 1 : entry.items du backend (v1.12.257)
                      const heroBack = findHero(entry?.items);
                      if (heroBack?.image_url) {
                        return <Image source={{ uri: heroBack.image_url }} style={styles.cellImage} resizeMode="contain" />;
                      }
                      // Source 2 : snapshot local (fallback)
                      const snap = snapshotsMap[entry?.date];
                      if (snap?.photo) {
                        return <Image source={{ uri: snap.photo }} style={styles.cellImage} resizeMode="contain" />;
                      }
                      const heroSnap = findHero(snap?.items);
                      if (heroSnap?.image_url) {
                        return <Image source={{ uri: heroSnap.image_url }} style={styles.cellImage} resizeMode="contain" />;
                      }
                      return null;
                    })()}
                    <View style={styles.cellNum}>
                      <Text style={[
                        styles.cellNumText,
                        entry && { color: '#fff', fontWeight: '700' },
                        today && !entry && { color: C.goldDark, fontWeight: '700' },
                      ]}>
                        {cell.day}
                      </Text>
                    </View>
                    {/* v1.12.233 — chip occasion + icône météo en bas de cellule */}
                    {entry && (entry.occasion || entry.weather_condition) && (
                      <View style={styles.cellMeta}>
                        {entry.occasion && (
                          <View style={[styles.cellOccChip, {
                            backgroundColor: getOccasionDisplay(entry.occasion).bg,
                          }]}>
                            <Text style={{ fontSize: 9 }}>{getOccasionDisplay(entry.occasion).emoji}</Text>
                          </View>
                        )}
                        {(entry.weather_condition || entry.weather?.condition) && (
                          <Text style={styles.cellWeather}>
                            {getWeatherIcon(entry.weather_condition || entry.weather?.condition) || ''}
                          </Text>
                        )}
                      </View>
                    )}
                    {entry?.rating && (
                      <View style={styles.cellRating}>
                        <Text style={{ fontSize: 8, color: '#fff', fontWeight: '700' }}>★ {entry.rating}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        )}

        {/* v1.12.233 — Bandeau aide première fois (dismissible) */}
        {helpBanner && (
          <CalendarFirstTimeHelp
            banner={helpBanner}
            userId={user?.id}
            onDismiss={() => setHelpBanner(null)}
          />
        )}

        {/* v1.12.233 — Stats du mois (basic+) ou teaser (free) */}
        <CalendarMonthStats
          stats={monthStats}
          tier={tier}
          monthLabel={getMonthLabel(year, monthIdx, lang)}
        />

        <Text style={styles.helpText}>
          {t('calendar_help_tap_day')}
        </Text>
      </ScrollView>

      {/* v1.12.225 — Modal d'ajout : Tenue du jour / Mes Looks / Photo manuelle */}
      <AddOutfitToCalendarModal
        visible={!!addModalDate}
        date={addModalDate}
        homeOutfit={homeOutfit}
        homeOutfitOptions={homeOutfitOptions}
        myLooks={myLooks}
        looksIA={looksIA}
        wardrobe={wardrobe}
        onClose={handleAddModalClose}
        onAdded={handleAddModalAdded}
      />

      {/* v1.12.236 — Modal détail journée (vue complète quand on clique une date avec entry) */}
      <CalendarDayDetailModal
        visible={!!detailEntry}
        entry={detailEntry}
        dateLabel={detailDateLabel}
        onClose={() => setDetailEntry(null)}
        onDeleted={() => { setDetailEntry(null); loadMonth(); }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: C.border,
  },
  headerTitle: { fontSize: 16, fontWeight: '700', color: C.text },
  // v1.12.236 — bouton retour : zone de touch ≥ 44×44 (Apple HIG) + chevron plus large
  backBtn: {
    width: 44, height: 44, borderRadius: 22,
    alignItems: 'center', justifyContent: 'center',
  },
  backIcon: { fontSize: 28, color: C.text, marginTop: -2, fontWeight: '300' },
  statsBanner: {
    backgroundColor: C.card, marginHorizontal: 12, marginTop: 8,
    padding: 10, borderRadius: 8, borderWidth: 1, borderColor: C.border,
    alignItems: 'center',
  },
  statsText: { fontSize: 13, fontWeight: '700', color: C.text },
  tierNote: { fontSize: 10, color: C.textFaint, marginTop: 2 },
  monthNav: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 10,
  },
  navBtn: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
    alignItems: 'center', justifyContent: 'center',
  },
  navBtnText: { fontSize: 18, color: C.text, fontWeight: '700' },
  monthLabel: { fontSize: 15, fontWeight: '700', color: C.text, letterSpacing: 0.3 },
  dowRow: { flexDirection: 'row', paddingHorizontal: 8, marginBottom: 2 },
  dowCell: { width: '14.2857%', alignItems: 'center', justifyContent: 'center', paddingVertical: 4 },
  dowLetter: { textAlign: 'center', fontSize: 10, fontWeight: '700', color: C.textFaint, letterSpacing: 0.5 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 8, marginTop: 2 },
  cellWrap: {
    width: '14.2857%',     // 1/7 → 7 cellules par ligne (lun → dim)
    aspectRatio: 1,        // carrée
    padding: 1,            // gap visuel entre cellules
  },
  cell: {
    flex: 1,
    backgroundColor: C.card, borderRadius: 8, borderWidth: 1, borderColor: C.border,
    overflow: 'hidden', position: 'relative',
  },
  cellWithEntry: { borderColor: C.gold, borderWidth: 2 },
  cellToday: { backgroundColor: '#FFF7E0' },
  cellImage: { ...StyleSheet.absoluteFillObject, opacity: 0.92 },
  cellNum: {
    position: 'absolute', top: 2, left: 4,
    backgroundColor: 'rgba(0,0,0,0.0)',
  },
  cellNumText: { fontSize: 12, color: C.textMuted },
  cellRating: {
    position: 'absolute', bottom: 2, right: 2,
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4,
  },
  // v1.12.233 — chip occasion + icône météo
  cellMeta: {
    position: 'absolute', bottom: 2, left: 2,
    flexDirection: 'row', alignItems: 'center', gap: 2,
  },
  cellOccChip: {
    paddingHorizontal: 3, paddingVertical: 1,
    borderRadius: 4,
  },
  cellWeather: { fontSize: 10 },
  helpText: {
    fontSize: 11, color: C.textFaint, textAlign: 'center',
    marginTop: 24, paddingHorizontal: 24, lineHeight: 16,
  },
});
